import React from 'react';
import { Edit2, FileText } from 'lucide-react';
import type { ClinicalNote } from '../../types/clinical';

export interface NoteCardProps {
  note: ClinicalNote;
  isDoctor: boolean;
  editingNoteId: string | null;
  editingNoteData: Partial<ClinicalNote>;
  editSaving: boolean;
  setEditingNoteId: (id: string | null) => void;
  setEditingNoteData: React.Dispatch<React.SetStateAction<Partial<ClinicalNote>>>;
  onSaveNoteEdit: () => void;
  onGenerateInvoice: (noteId: string) => void;
}

export function NoteCard({
  note,
  isDoctor,
  editingNoteId,
  editingNoteData,
  editSaving,
  setEditingNoteId,
  setEditingNoteData,
  onSaveNoteEdit,
  onGenerateInvoice,
}: NoteCardProps) {
  if (note.noteType === 'manual') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-amber-400 p-4">
        {editingNoteId === note.id ? (
          /* Inline edit form */
          <div className="space-y-2">
            <input value={editingNoteData.title || ''} onChange={e => setEditingNoteData(d => ({ ...d, title: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" placeholder="Title" />
            <input type="date" value={editingNoteData.date || ''} onChange={e => setEditingNoteData(d => ({ ...d, date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            <textarea value={editingNoteData.content || ''} onChange={e => setEditingNoteData(d => ({ ...d, content: e.target.value }))} rows={4} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            <div className="flex gap-2">
              <button onClick={onSaveNoteEdit} disabled={editSaving} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{editSaving ? 'Saving...' : 'Save Changes'}</button>
              <button onClick={() => setEditingNoteId(null)} className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">Manual Note</span>
                <span className="text-xs text-gray-500">{note.date || new Date(note.createdAt).toLocaleDateString()}</span>
              </div>
              {isDoctor && (
                <button onClick={() => { setEditingNoteId(note.id); setEditingNoteData({ title: note.title, content: note.content, date: note.date || '' }); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Edit2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{note.title}</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{note.content}</p>
            {note.updatedAt && <p className="text-xs text-gray-400 mt-2">Edited {new Date(note.updatedAt).toLocaleDateString()}</p>}
          </>
        )}
      </div>
    );
  }

  // SOAP Note card
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-blue-500 p-4">
      {editingNoteId === note.id ? (
        /* Inline edit form for SOAP note */
        <div className="space-y-2">
          {(['subjective', 'objective', 'assessment', 'plan'] as const).map(field => (
            <div key={field}>
              <label className="block text-xs font-medium text-gray-500 mb-1 capitalize">{field}</label>
              <textarea value={(editingNoteData[field] as string) || ''} onChange={e => setEditingNoteData(d => ({ ...d, [field]: e.target.value }))} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={onSaveNoteEdit} disabled={editSaving} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{editSaving ? 'Saving...' : 'Save Changes'}</button>
            <button onClick={() => setEditingNoteId(null)} className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Consultation</span>
              <span className="text-xs text-gray-500">{new Date(note.createdAt).toLocaleString()}</span>
              {note.hospitalName && <span className="text-xs text-gray-500">— {note.hospitalName}</span>}
            </div>
            <div className="flex items-center gap-2">
              {isDoctor && (
                <button onClick={() => { setEditingNoteId(note.id); setEditingNoteData({ subjective: note.subjective, objective: note.objective, assessment: note.assessment, plan: note.plan, icd10Confirmed: note.icd10Confirmed, nhrplCodes: note.nhrplCodes }); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Edit2 className="h-3.5 w-3.5" /></button>
              )}
              {isDoctor && (
                <button onClick={() => onGenerateInvoice(note.id)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Generate Invoice</button>
              )}
            </div>
          </div>
          {note.vitalSigns && Object.values(note.vitalSigns).some(v => v) && (
            <div className="flex gap-4 mb-2 text-xs text-gray-500">
              {note.vitalSigns.bp && <span>BP: {note.vitalSigns.bp}</span>}
              {note.vitalSigns.weight && <span>Wt: {note.vitalSigns.weight}kg</span>}
              {note.vitalSigns.fundalHeight && <span>FH: {note.vitalSigns.fundalHeight}</span>}
              {note.vitalSigns.fhr && <span>FHR: {note.vitalSigns.fhr}</span>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><span className="font-semibold text-gray-700">S:</span> <span className="text-gray-600">{note.subjective}</span></div>
            <div><span className="font-semibold text-gray-700">O:</span> <span className="text-gray-600">{note.objective}</span></div>
            <div><span className="font-semibold text-gray-700">A:</span> <span className="text-gray-600">{note.assessment}</span></div>
            <div><span className="font-semibold text-gray-700">P:</span> <span className="text-gray-600">{note.plan}</span></div>
          </div>
          {note.icd10Confirmed.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {note.icd10Confirmed.map(code => (
                <span key={code} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full">{code}</span>
              ))}
            </div>
          )}
          {note.updatedAt && <p className="text-xs text-gray-400 mt-2">Edited {new Date(note.updatedAt).toLocaleDateString()}</p>}
        </>
      )}
    </div>
  );
}
